/**
 * Busy guard — "this button is working, please wait".
 *
 * Why this exists
 * ---------------
 * Most drawers have to fetch their data before they can be shown (the invoice
 * editor alone asks the server for the document, the ledger lists, the prefixes
 * and the GST scheme). During that gap nothing on screen changes, so the user
 * clicks the same button again — and a second copy of the same drawer opens on
 * top of the first.
 *
 * What it does, for every button on every page:
 *   1. The moment a click starts network work, the button shows a small spinner
 *      so the user can see the click landed and something is loading.
 *   2. While that work is still running, further clicks on the SAME button are
 *      swallowed, so the same drawer can never be opened twice.
 *   3. As soon as the drawer appears (or the network goes quiet), the button
 *      goes back to normal.
 *
 * Buttons that do purely local work are untouched: the guard only takes hold
 * once it sees a request in flight, so rapid clicking on things like "Add row"
 * keeps working exactly as before.
 *
 * Bundles are built one-per-page (esbuild), so the shared counters live on
 * `window` — every bundle on the page talks to the same guard.
 */

/** Clicking any of these arms the guard. */
const GUARD_SELECTOR = 'button, a.btn, [role="button"], [data-busy-guard]';

/** ...unless the click landed inside one of these. */
const SKIP_SELECTOR = [
  '.dropdown-item',            // combo-box / menu rows — the menu closes on pick anyway
  '.dropdown-toggle',          // opening a menu is local work
  '.btn-close',
  '[data-drawer-save]',        // the drawer footer runs its own "Saving…" spinner
  '[data-drawer-cancel]',
  '[data-no-busy]',            // opt-out for anything that manages its own busy state
].join(',');

const TICK_MS = 80;
/** ~240 ms of no network activity means the click did only local work. */
const IDLE_TICKS_TO_RELEASE = 3;
/** Never hold a button hostage longer than this, whatever happens. */
const MAX_BUSY_MS = 20000;

interface GuardShared {
  /** Number of apiClient requests currently in flight, across every bundle. */
  pending: number;
  /** Called by the drawer as it comes on screen — the wait is over. */
  drawerOpened(): void;
}

function shared(): GuardShared {
  const w = window as unknown as { __acteazeBusyGuard?: GuardShared };
  if (!w.__acteazeBusyGuard) {
    w.__acteazeBusyGuard = { pending: 0, drawerOpened: () => { /* set on install */ } };
  }
  return w.__acteazeBusyGuard;
}

/**
 * Wrapped around every apiClient call so the guard knows work is in flight.
 * Returns the "finished" callback — safe to call more than once.
 */
export function beginTrackedRequest(): () => void {
  const state = shared();
  state.pending++;
  let done = false;
  return () => {
    if (done) return;
    done = true;
    state.pending = Math.max(0, state.pending - 1);
  };
}

/** Called by openDrawer the moment the drawer is put on screen. */
export function notifyDrawerOpened(): void {
  shared().drawerOpened();
}

let armedEl: HTMLElement | null = null;
let armedAt = 0;
let timer: number | null = null;
let idleTicks = 0;
let busy = false;

function showBusy(el: HTMLElement): void {
  if (el.querySelector(':scope > .ae-busy-spinner')) return;
  el.classList.add('ae-busy');
  el.setAttribute('aria-busy', 'true');
  const spinner = document.createElement('span');
  const hasLabel = (el.textContent ?? '').trim().length > 0;
  spinner.className = 'spinner-border spinner-border-sm ae-busy-spinner' + (hasLabel ? ' me-1' : '');
  spinner.setAttribute('role', 'status');
  spinner.setAttribute('aria-hidden', 'true');
  el.prepend(spinner);
}

function clearBusy(el: HTMLElement): void {
  el.classList.remove('ae-busy');
  el.removeAttribute('aria-busy');
  el.querySelectorAll(':scope > .ae-busy-spinner').forEach(node => node.remove());
}

function release(): void {
  if (timer !== null) {
    window.clearInterval(timer);
    timer = null;
  }
  if (armedEl) clearBusy(armedEl);
  armedEl = null;
  busy = false;
  idleTicks = 0;
}

function tick(): void {
  const el = armedEl;
  if (!el || !el.isConnected) { release(); return; }
  if (Date.now() - armedAt > MAX_BUSY_MS) { release(); return; }
  if (shared().pending > 0) {
    idleTicks = 0;
    if (!busy) {
      busy = true;
      showBusy(el);
    }
    return;
  }
  idleTicks++;
  if (idleTicks >= IDLE_TICKS_TO_RELEASE) release();
}

function arm(el: HTMLElement): void {
  release();
  armedEl = el;
  armedAt = Date.now();
  idleTicks = 0;
  timer = window.setInterval(tick, TICK_MS);
}

function onClickCapture(e: MouseEvent): void {
  if (e.button !== 0) return;
  const target = e.target as Element | null;
  if (!target || typeof target.closest !== 'function') return;
  const el = target.closest<HTMLElement>(GUARD_SELECTOR);
  if (!el || el.closest(SKIP_SELECTOR)) return;

  if (el === armedEl && busy) {
    // The first click is still loading. Drop this one so the user doesn't end up
    // with two copies of the same drawer.
    e.preventDefault();
    e.stopImmediatePropagation();
    return;
  }
  arm(el);
}

export function installBusyGuard(): void {
  if (typeof document === 'undefined') return;
  const doc = document as unknown as { __acteazeBusyGuardWired?: boolean };
  if (doc.__acteazeBusyGuardWired) return;
  doc.__acteazeBusyGuardWired = true;
  // Capture phase, so a repeat click is dropped before any page handler sees it.
  document.addEventListener('click', onClickCapture, true);
  shared().drawerOpened = () => { if (armedEl) release(); };
}

installBusyGuard();
